import { createRoot } from "react-dom/client";
import { Sentry, initSentry } from "./lib/sentry";
import { initAnalytics } from "./lib/analytics";
import { initMetaPixel, setMetaConsent } from "./lib/metaPixel";
import { initMetricool } from "./lib/metricool";
import { setAnalyticsConsent } from "./lib/analytics";
import { setOneSignalConsentAllowed } from "./lib/oneSignal";
import { getConsent, onConsentChange, type ConsentState } from "./lib/consent";
import { captureFirstTouch } from "./lib/attribution";
import { SentryFallback } from "./components/SentryFallback";
import BootReadySignal from "./components/BootReadySignal";
import { DebugOverlay } from "./components/DebugOverlay";
import App from "./App.tsx";
import { resolveBootLang } from "./i18n/locale";
import { loadBootLocales } from "./i18n/translations";
import "./index.css";
import { APP_VERSION } from "./lib/appVersion";

// Deploy triage: which bundle is this device actually running? Compare with
// the served /version.json and the stamp in Settings.
console.info("[VYV] build " + (APP_VERSION || "dev"));

// Primeiro toque de aquisição, ANTES de qualquer coisa que possa reescrever a
// URL (router, redirect de auth). First-write-wins: só grava se ainda não
// houver um primeiro toque, então recarregar a página não sobrescreve nada.
// Deliberadamente fora do portão de consent: não é analytics de terceiro, é
// um dado do próprio cadastro, gravado no nosso banco. Ver src/lib/attribution.ts.
captureFirstTouch();

initSentry();
initAnalytics();
// LGPD consent (Termly banner in index.html) — applied BEFORE the pixel init
// so a stored Decline lands as fbq consent-revoke pre-init. Categories:
// analytics -> PostHog; advertising -> Meta Pixel + OneSignal push.
const applyConsent = (c: ConsentState) => {
  setMetaConsent(c.advertising);
  setAnalyticsConsent(c.analytics);
  setOneSignalConsentAllowed(c.advertising);
};
applyConsent(getConsent());
// Meta Pixel (ads) — no-op unless VITE_META_PIXEL_ID is set. PostHog above
// stays the internal source of truth; the pixel only mirrors funnel events.
initMetaPixel();
onConsentChange(applyConsent);
// Public marketing analytics uses Termly's documented consent API (fail closed).
initMetricool();

// ── O DICIONÁRIO ANTES DO PRIMEIRO FRAME ────────────────────────────────────
//
// Os nove locales saíram do chunk de entrada (ver src/i18n/translations.ts) e
// viraram um chunk por idioma, carregado sob demanda. Isso cria uma pergunta
// que não existia: em que momento o dicionário do usuário chega?
//
// A resposta NÃO é "gate no React". O LanguageProvider pinta o idioma
// persistido no primeiro frame de propósito, sem espera — um <Suspense> ali
// trocaria 730 KB de download por um flash de idioma errado (ou de chave crua)
// em todo boot, que é pior do que o problema original.
//
// A resposta é ORDEM: esperamos o dicionário AQUI, antes de existir um primeiro
// frame para errar. O custo é coberto por duas coisas que já estavam de pé:
//
//   1. o splash estático do index.html, pintado pelo browser antes deste
//      bundle sequer parsear, e removido só depois do primeiro commit do React;
//   2. o <link rel="modulepreload"> que o index.html injeta para o chunk do
//      idioma persistido — ele começa a baixar JUNTO com o bundle principal,
//      não depois. Em rede lenta o dicionário (~91 KB gzip) chega muito antes
//      do bundle (~1,2 MB gzip), então o await abaixo tipicamente resolve na
//      hora. Ver o plugin `localePreloadPlugin` em vite.config.ts.
//
// `resolveBootLang()` é a MESMA função que o LanguageProvider usa no estado
// inicial. Tem de ser a mesma: se este arquivo baixasse "PT-BR" e o provider
// decidisse "EN", o frame 1 pintaria chave crua — exatamente o que se quer
// evitar. `src/test/localeFirstPaint.test.tsx` trava os dois call sites.
//
// IIFE async em vez de top-level await de propósito: o alvo de build do Vite
// inclui Safari 14, que não tem TLA — o build falharia.
void (async () => {
  try {
    await loadBootLocales(resolveBootLang());
  } catch {
    // loadLocale já logou (logo, Sentry). Renderizar assim mesmo é melhor que
    // não renderizar: a tela mostra as CHAVES, feia mas viva e diagnosticável,
    // e uma troca de idioma no seletor recarrega o dicionário.
  }

  createRoot(document.getElementById("root")!).render(
    <>
      {import.meta.env.VITE_DEBUG_OVERLAY === 'true' && <DebugOverlay />}
      <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
        <App />
      </Sentry.ErrorBoundary>
      <BootReadySignal />
    </>
  );


})();
